/**
 * The OneOf union type allows for some complex and type-safe unions. This is heavily inspired by the Oneof type from
 * the Protocol Buffers spec. he OneOf type is basically some syntactic sugar for discriminated unions and allow for
 * easy exhaustiveness checking.
 *
 * The OneOf union type is very explicit and allows us to type every possible response from a function. We can easily
 * type errors and different response types. This does have the downside that it's more cumbersome to use... However,
 * I think that the benefits of the OneOf type far outweigh the draw backs.
 *
 * @example Basic example
 * type GetUserResponse = OneOf<{ user: User; notFound: UserNotFoundError }>;
 * function getUser(id: number): Promise<GetUserResponse> {
 *     const user = db.main.Users.findById(id);
 *     return !!user
 *         ? { type: 'user', value: user }
 *         : { type: 'notFound', value: new UserNotFoundError(id) };
 * }
 * @example Using the result wrapper function
 * type GetUserResponse = OneOf<{ user: User; notFound: UserNotFoundError }>;
 * function getUser(id: number): Promise<GetUserResponse> {
 *     const user = db.main.Users.findById(id);
 *     return !!user
 *         ? result({ user })
 *         : result({ notFound: new UserNotFoundError(id) })
 * }
 * @example Consume a OneOf response
 * type GetUserResponse = OneOf<{ user: User; notFound: UserNotFoundError }>;
 * function handleUserResponse(userResponse: GetUserResponse) {
 *     if (userResponse.type === 'user') {
 *         console.log('Username: ' + user.value.username);
 *     } else if (userResponse.type === 'notFound') {
 *         console.error('The user was not found');
 *     } else {
 *         throw assertNever(userResponse);
 *     }
 * }
 * @see {@link https://developers.google.com/protocol-buffers/docs/proto3#oneof Protocol Buffers Oneof type}
 * @see {@link https://www.typescriptlang.org/docs/handbook/advanced-types.html#discriminated-unions Discriminated Unions}
 * @see {@link https://www.typescriptlang.org/docs/handbook/advanced-types.html#exhaustiveness-checking Exhaustiveness Checking}
 */
export type OneOf<T extends Record<any, any>> = { [K in keyof T]: { type: K; value: T[K] } }[keyof T];

/**
 * This is a wrapper function for easily creating OneOf types.
 *
 * @example
 * // Bloated and annoying to write
 * return { type: 'user', value: user };
 * // Super easy and clean
 * return result({ user });
 * @param value - An object containing a single key/value that matches one of the types defined in the OneOf union
 * @returns A Oneof union value for the given value
 */
export function result<TKey extends string | symbol, TValue>(value: { [K in TKey]: TValue }): {
    type: TKey;
    value: TValue;
} {
    const keys = Object.keys(value) as TKey[];

    if (keys.length !== 1) {
        throw new Error(`Result value should only have one property. Found ${keys.length}: ${keys}`);
    }

    const key = keys[0];
    return { type: key, value: value[key] };
}
